import { createBrowserRouter, Link } from 'react-router';
import { Button, EmptyState, ErrorState, Spinner } from '@zentera/ui';
import { AppShell } from './components/app-shell';
import { RequireAuth } from './lib/auth';
import { LoginPage, MfaSetupPage, MfaVerifyPage, PasswordChangePage } from './pages/auth';
import { OverviewPage } from './pages/overview';

function NotFound() {
  return (
    <EmptyState
      title="Página não encontrada"
      description="O endereço não existe ou foi movido."
      action={
        <Button asChild>
          <Link to="/">Ir para a visão geral</Link>
        </Button>
      }
    />
  );
}

function RouteError() {
  return <ErrorState title="Falha ao carregar a página" error="Recarregue o navegador. Se persistir, uma nova versão pode ter sido publicada." onRetry={() => window.location.reload()} />;
}

function Pending() {
  return (
    <div className="flex h-40 items-center justify-center text-fg-3">
      <Spinner />
    </div>
  );
}

/** Telas carregadas sob demanda: o pacote inicial contém apenas login, layout e visão geral. */
const page = <K extends string>(load: () => Promise<Record<K, React.ComponentType>>, name: K) => ({
  lazy: async () => ({ Component: (await load())[name] }),
  errorElement: <RouteError />,
});

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/login/mfa', element: <MfaVerifyPage /> },
  { path: '/setup/mfa', element: <MfaSetupPage /> },
  { path: '/setup/password', element: <PasswordChangePage /> },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    hydrateFallbackElement: <Pending />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: 'providers', ...page(() => import('./pages/providers'), 'ProvidersPage') },
      { path: 'providers/new', ...page(() => import('./pages/provider-new'), 'ProviderNewPage') },
      { path: 'providers/:id/:tab?', ...page(() => import('./pages/provider-detail'), 'ProviderDetailPage') },
      { path: 'infrastructure', ...page(() => import('./pages/infrastructure'), 'InfrastructurePage') },
      { path: 'infrastructure/:id', ...page(() => import('./pages/instance-detail'), 'InstanceDetailPage') },
      { path: 'agents', ...page(() => import('./pages/agents'), 'AgentsPage') },
      { path: 'agents/:id', ...page(() => import('./pages/agent-detail'), 'AgentDetailPage') },
      { path: 'knowledge', ...page(() => import('./pages/knowledge'), 'KnowledgePage') },
      { path: 'integrations', ...page(() => import('./pages/integrations'), 'IntegrationsPage') },
      { path: 'integrations/connectors/:key/:tab?', ...page(() => import('./pages/connector-detail'), 'ConnectorDetailPage') },
      { path: 'usage', ...page(() => import('./pages/usage'), 'UsagePage') },
      { path: 'security/:tab?', ...page(() => import('./pages/security'), 'SecurityPage') },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
